import { inject, Injectable, Signal } from "@angular/core";
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  concatMap,
  Observable,
  of,
  retryWhen,
  switchMap,
  tap,
  throwError,
  timer,
  map,
} from "rxjs";
import { toSignal } from "@angular/core/rxjs-interop";
import { encode64 } from "../../../helper/encode.function";
import { decode64 } from "../../../helper/decode.function";
import {
  UpdateClinicDto,
  UpdateDoctorDto,
  UpdatePatientDto,
  UpdatePsychoDto,
} from "../../../../api/models";
import { CachedDoctorsService } from "../../../../services";
import { CachedPatientsService } from "../../../../services";
import { CognitoUtilsService } from "./cognito-utils.service";
import { CognitoUserSession } from "amazon-cognito-identity-js";
import { CachedClinicsService } from "../../../../services";
import { CachedPsychologistsService } from "../../../../services";
import { UserIdentifiers } from "../interfaces/user-identifiers.interface";

/**
 * Logged User Service
 * Follows Kosmo pattern - stateless, data loaded from backend on demand
 */
@Injectable({
  providedIn: "root",
})
export class LoggedUserService {
  /** Cognito utilities service */
  private readonly _cognitoUtilsService = inject(CognitoUtilsService);

  /** The cached doctors service */
  private readonly _cachedDoctorsService: CachedDoctorsService =
    inject(CachedDoctorsService);

  /** The cached patients service */
  private readonly _cachedPatientsService: CachedPatientsService = inject(
    CachedPatientsService,
  );

  /** The cached clinics service */
  private readonly _cachedClinicsService: CachedClinicsService =
    inject(CachedClinicsService);

  /** The cached psychologists service */
  private readonly _cachedPsychologistsService: CachedPsychologistsService =
    inject(CachedPsychologistsService);

  /** The user subject - follows Kosmo pattern with BehaviorSubject */
  private readonly _userSubject = new BehaviorSubject<any | null>(null);

  /** The user observable */
  public readonly user$: Observable<any | null> = this._userSubject.asObservable();
  
  /** The user as a reactive Signal - properly connected to Angular's change detection */
  public readonly currentUser: Signal<any | null> = toSignal(this.user$, { initialValue: null });


  /**
   * Get user - follows Kosmo pattern: returns from memory or loads from backend
   * @param forceRefresh Force reload from backend
   * @returns Observable of user data
   */
  public getUser(forceRefresh = false): Observable<any | null> {
    const currentUser = this._userSubject.value;
    
    // If user exists in memory and not forcing refresh, return it directly
    if (currentUser && !forceRefresh) {
      return of(currentUser);
    }
    
    // Otherwise load from backend
    return this._loadUserFromBackend();
  }

  /**
   * Load user from backend based on Cognito data
   * Follows Kosmo pattern - no localStorage, only backend
   * @returns the user observable
   */
  private _loadUserFromBackend(): Observable<any | null> {
    return this.getUserIdentifiers$().pipe(
      switchMap(({ tenant, sub, group }) => {
        if (!tenant || !sub || !group) {
          return of(null);
        }
        const userObservable: Observable<any | null> =
          this._manageFindUserByGroup(group.toLowerCase(), sub);

        return userObservable.pipe(
          // Retry con backoff esponenziale per gestire la creazione asincrona dell'utente
          // Synchronized with RegistrationUserInformationComponent timeout (120s)
          retryWhen((errors) =>
            errors.pipe(
              concatMap((error, index) => {
                // Retry solo per errori 404 (utente non ancora creato) o errori di rete
                // Aumentato a 15 tentativi (era 5) per matchare component polling (120s)
                if ((error.status === 404 || error.status === 0) && index < 15) {
                  // Backoff esponenziale capped a 8s: 1s, 2s, 4s, 8s, 8s, 8s...
                  // Total: 1+2+4+8+8+8+8+8+8+8+8+8+8+8+8 = 115s (~120s)
                  const delay = Math.min(Math.pow(2, index), 8) * 1000;
                  console.log(`[LoggedUserService] User not found (404), retry ${index + 1}/15 after ${delay}ms`);
                  return timer(delay);
                }
                // Per altri errori o dopo 15 tentativi, propaga l'errore
                console.error(`[LoggedUserService] Stopping retries after ${index} attempts or non-404 error`);
                return throwError(() => error);
              }),
            ),
          ),
        );
      }),
      tap((user) => {
        if (user) {
          // Update BehaviorSubject - no localStorage
          this._userSubject.next(user);
        }
      }),
      catchError((error) => {
        return of(null);
      }),
    );
  }
  
  /**
   * Legacy method for backward compatibility
   * @deprecated Use getUser() instead
   */
  public loadUserFromBackend$(): Observable<any | null> {
    return this.getUser(true);
  }

  /**
   * Manage the switch between user groups
   * @param userGroup the user group to switch to
   * @param userSub the user's sub
   * @returns an observable of the user's data
   */
  private _manageFindUserByGroup(
    userGroup: string,
    userSub: string,
  ): Observable<any | null> {
    switch (userGroup) {
      case "doctor":
        return this._cachedDoctorsService.findOne$(userSub);
      case "patient":
        return this._cachedPatientsService.findOne$(userSub);
      case "clinic":
        return this._cachedClinicsService.findOne$(userSub);
      case "psycho":
        return this._cachedPsychologistsService.findOne$(userSub);
      default:
        return of(null);
    }
  }

  /**
   * Initialize user - follows Kosmo pattern, always loads from backend
   * @returns Observable of the loaded user
   */
  public initializeUser$(): Observable<any | null> {
    return this.getUser();
  }

  /**
   * Clear user data on logout - follows Kosmo pattern
   */
  public clearUser(): void {
    this._userSubject.next(null);
  }

  /**
   * Get user identifiers from Cognito token as Observable
   * @returns Observable with tenant, sub and group
   */
  public getUserIdentifiers$(): Observable<UserIdentifiers> {
    const user = this._cognitoUtilsService.getCurrentUser();

    if (!user) {
      return of({ tenant: "", sub: "", group: "", isFederated: false });
    }

    return new Observable((observer) => {
      user.getSession(
        (err: Error | null, session: CognitoUserSession | null) => {
          if (err || !session) {
            observer.next({
              tenant: "",
              sub: "",
              group: "",
              isFederated: false,
            });
            observer.complete();
            return;
          }

          const authToken = session.getAccessToken().decodePayload();
          const idToken = session.getIdToken().decodePayload();
          

          observer.next({
            tenant: authToken?.["tenantId"] || "",
            sub: authToken?.["sub"] || "",
            group:
              authToken?.["userType"] ||
              authToken?.["cognito:groups"]?.[0] ||
              "",
            isFederated:
              (idToken?.["identities"] &&
                idToken?.["identities"][0]?.providerName === "Google") ||
              false,
          });
          observer.complete();
        },
      );
    });
  }

  /**
   * Update user data - in memory only, follows Kosmo pattern
   * @param userData Updated user data
   */
  public updateUserData(userData: any): void {
    this._userSubject.next(userData);
  }

  /**
   * Update logged user data
   * @param updateUserDto Updated user data
   * @returns Observable of updated user data
   */
  public updateLoggedUser$(updateUserDto: any): Observable<any | null> {
    return this.getUserIdentifiers$().pipe(
      switchMap(({ sub, group }) => {
        const update$: Observable<any> = this._manageUpdateUserByGroup(
          group,
          sub,
          updateUserDto,
        );

        return update$;
      }),
      tap((updatedUser) => {
        if (updatedUser) {
          this.updateUserData(updatedUser);
        }
      }),
      catchError((error) => {
        console.error("Errore durante l'update del profilo:", error);
        return throwError(() => error);
      }),
    );
  }

  /**
   * Manage update user by group
   * @param userGroup the user group
   * @param userSub the user sub
   * @param updateUserDto the updated user data
   * @returns the updated user data
   */
  private _manageUpdateUserByGroup(
    userGroup: string,
    userSub: string,
    updateUserDto:
      | UpdateDoctorDto
      | UpdatePatientDto
      | UpdateClinicDto
      | UpdatePsychoDto,
  ): Observable<any | null> {
    switch (userGroup.toLowerCase()) {
      case "doctor":
        return this._cachedDoctorsService.update$(
          userSub,
          updateUserDto as UpdateDoctorDto,
        );
      case "patient":
        return this._cachedPatientsService.update$(
          userSub,
          updateUserDto as UpdatePatientDto,
        );
      case "clinic":
        return this._cachedClinicsService.update$(
          userSub,
          updateUserDto as UpdateClinicDto,
        );
      case "psycho":
        return this._cachedPsychologistsService.update$(
          userSub,
          updateUserDto as UpdatePsychoDto,
        );
      default:
        console.error("Gruppo utente sconosciuto:", userGroup);
        return of(null);
    }
  }
}
